// Teklif detay sayfası
import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  FiEdit, 
  FiTrash2,
  FiFileText,
  FiCalendar,
  FiUser,
  FiBuilding,
  FiDollarSign,
  FiClock,
  FiTag,
  FiDownload,
  FiSend,
  FiCheck,
  FiX,
  FiAlertCircle,
  FiInfo,
  FiTrendingUp,
  FiCheckCircle
} from 'react-icons/fi';
import { CgMenuGridO } from 'react-icons/cg';
import { IoChevronBackOutline } from 'react-icons/io5';
import * as offerService from '../../services/offerService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import DeleteConfirmModal from '../../components/Common/DeleteConfirmModal';
import CurrencyInput from 'react-currency-input-field';
import toast from 'react-hot-toast';

const OfferDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [offerDescription, setOfferDescription] = useState('');
  const [offerTotal, setOfferTotal] = useState('');
  const [offerProfitability, setOfferProfitability] = useState('');
  const [offerCurrency, setOfferCurrency] = useState('TRY');
  const [offerStatus, setOfferStatus] = useState('Hazırlandı');
  const [offerDate, setOfferDate] = useState('');
  const [offerTime, setOfferTime] = useState('');
  const [offerFile, setOfferFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadMessageType, setUploadMessageType] = useState('');

  // Teklif detaylarını al
  const { data: offer, isLoading, error } = useQuery(
    ['offer', id],
    () => offerService.getOffer(id),
    {
      enabled: !!id,
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Teklif yüklenemedi');
        navigate('/offers');
      }
    }
  );

  // Teklif silme mutation'ı
  const deleteOfferMutation = useMutation(
    offerService.deleteOffer,
    {
      onSuccess: () => {
        toast.success('Teklif başarıyla silindi');
        navigate('/offers');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Teklif silinemedi');
      }
    }
  );

  // Durum güncelleme mutation'ı
  const updateStatusMutation = useMutation(
    ({ offerId, status }) => offerService.updateOfferStatus(offerId, status),
    {
      onSuccess: () => {
        toast.success('Teklif durumu güncellendi');
        queryClient.invalidateQueries(['offer', id]);
        setShowStatusModal(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Durum güncellenemedi');
      }
    }
  );

  // Teklif durumu metinleri
  const getStatusText = (status) => {
    switch (status) {
      case 'draft': return 'Taslak';
      case 'sent': return 'Gönderildi';
      case 'accepted': return 'Kabul Edildi';
      case 'rejected': return 'Reddedildi';
      case 'expired': return 'Süresi Doldu';
      default: return status;
    }
  };

  // Durum renkleri
  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'sent': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'accepted': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'expired': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Para birimi formatı
  const formatCurrency = (amount, currency) => {
    const currencyCode = (currency === 'TL' || !currency) ? 'TRY' : currency;
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Number(amount || 0));
  };

  // Silme onayı
  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  // Dosya indirme
  const handleDownloadFile = async () => {
    if (!offer.offer_file_path) {
      toast.error('Dosya bulunamadı');
      return;
    }

    try {
      const response = await offerService.downloadOfferFile(offer.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = offer.offer_file_name || 'teklif-dosyasi.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Dosya indirildi');
    } catch (error) {
      toast.error('Dosya indirilemedi');
    }
  };

  const confirmDelete = () => {
    deleteOfferMutation.mutate(id);
  };

  // Teklif düzenleme
  const handleEditOffer = () => {
    setEditingOffer(offer);
    setOfferDescription(offer.offer_file_description || offer.offer_description || '');
    setOfferTotal(offer.offer_total || offer.offer_amount || '');
    setOfferProfitability(offer.profitability || '');
    setOfferCurrency(offer.currency || offer.offer_currency || 'TRY');
    setOfferStatus(offer.offer_status || 'Hazırlandı');
    setOfferDate(offer.created_at ? offer.created_at.split('T')[0] : '');
    setOfferTime(offer.created_at ? offer.created_at.split('T')[1]?.substring(0, 5) : '');
    setOfferFile(null);
    setShowEditModal(true);
    setShowActionsMenu(false);
  };

  // Teklif güncelleme
  const handleUpdateOffer = async () => {
    if (!editingOffer) return;
    setIsUploading(true);
    setUploadMessage('');
    setUploadMessageType('');
    
    try {
      const formData = new FormData();
      formData.append('offer_file_description', offerDescription || '');
      formData.append('offer_total', offerTotal || '');
      formData.append('profitability', offerProfitability || '');
      formData.append('currency', offerCurrency || 'TRY');
      formData.append('offer_status', offerStatus || 'Hazırlandı');
      const combinedDateTime = offerDate && offerTime ? `${offerDate}T${offerTime}:00` : new Date().toISOString();
      formData.append('created_at', combinedDateTime);
      if (offerFile) {
        formData.append('offer_file', offerFile);
      }
      
      await offerService.updateOffer(editingOffer.id, formData);
      toast.success('Teklif güncellendi');
      setShowEditModal(false);
      setEditingOffer(null);
      queryClient.invalidateQueries(['offer', id]);
    } catch (error) {
      const msg = error.response?.data?.message || 'Teklif güncellenemedi';
      toast.error(msg);
      setUploadMessage(msg);
      setUploadMessageType('error');
    } finally {
      setIsUploading(false);
    }
  };

  // Menü dışına tıklandığında menüyü kapat
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionsMenu && !event.target.closest('.actions-menu')) {
        setShowActionsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionsMenu]);

  // Durum güncelleme
  const handleStatusUpdate = (status) => {
    setNewStatus(status);
    setShowStatusModal(true);
  };

  const confirmStatusUpdate = () => {
    updateStatusMutation.mutate({ offerId: id, status: newStatus });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !offer) {
    return (
      <div className="text-center py-12">
        <FiAlertCircle size={48} className="mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Teklif Bulunamadı
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Aradığınız teklif bulunamadı veya erişim yetkiniz bulunmuyor.
        </p>
        <Link to="/offers" className="btn-primary">
          Tekliflere Dön
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sayfa başlığı */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => window.history.back()}
            className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            title="Geri"
          >
            <IoChevronBackOutline size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Teklif ID: #{offer.id}
            </h1>
          
          </div>
        </div>

        {/* İşlemler Menüsü */}
        <div className="relative actions-menu">
          <button
            onClick={() => setShowActionsMenu(!showActionsMenu)}
            className="btn-primary flex items-center"
          >
            <CgMenuGridO className="mr-2" size={16} />
            İşlemler
          </button>

          {showActionsMenu && (
            <div className="absolute top-full right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-xl z-50 min-w-48">
              <div className="py-1">
                {offer.offer_status === 'draft' && (
                  <button
                    onClick={() => {
                      handleStatusUpdate('sent');
                      setShowActionsMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    disabled={updateStatusMutation.isLoading}
                  >
                    <FiSend className="mr-3" size={16} /> Gönder
                  </button>
                )}
                
                {offer.offer_status === 'sent' && (
                  <>
                    <button
                      onClick={() => {
                        handleStatusUpdate('accepted');
                        setShowActionsMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      disabled={updateStatusMutation.isLoading}
                    >
                      <FiCheck className="mr-3" size={16} /> Kabul Et
                    </button>
                    <button
                      onClick={() => {
                        handleStatusUpdate('rejected');
                        setShowActionsMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      disabled={updateStatusMutation.isLoading}
                    >
                      <FiX className="mr-3" size={16} /> Reddet
                    </button>
                  </>
                )}

                {(currentUser?.user_type === 'admin' || 
                  currentUser?.user_type === 'manager' || 
                  offer.created_by === currentUser?.id) && (
                  <>
                    <button
                      onClick={handleEditOffer}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <FiEdit className="mr-3" size={16} /> Düzenle
                    </button>
                    
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      disabled={deleteOfferMutation.isLoading}
                    >
                      <FiTrash2 className="mr-3" size={16} /> Sil
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Teklif Bilgileri */}
      <div className="card p-0 overflow-hidden">
        <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Teklif Bilgileri
          </h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {/* 0. Teklif Tipi */}
            <div className="flex items-start space-x-3">
              <FiTag className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Teklif Tipi
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {offer.project_name ? 'Proje Teklifi' : 'Müşteri Teklifi'}
                </p>
              </div>
            </div>

            {/* 1. Teklif Numarası */}
            <div className="flex items-start space-x-3">
              <FiFileText className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Teklif Numarası
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  #{offer.offer_number || offer.id}
                </p>
              </div>
            </div>

            {/* 2. Teklif Durumu */}
            <div className="flex items-start space-x-3">
              <FiCheckCircle className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Teklif Durumu
                </p>
                <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusColor(offer.offer_status)}`}>
                  {getStatusText(offer.offer_status)}
                </span>
              </div>
            </div>

            {/* 3. Teklif Description */}
            <div className="flex items-start space-x-3">
              <FiInfo className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Teklif Açıklaması
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {offer.offer_file_description || offer.offer_description || 'Açıklama yok'}
                </p>
              </div>
            </div>

            {/* 4. Teklif Tutarı - PARA BİRİMİ */}
            <div className="flex items-start space-x-3">
              <FiDollarSign className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Teklif Tutarı
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {formatCurrency(offer.offer_total || offer.offer_amount, offer.currency || offer.offer_currency)}
                </p>
              </div>
            </div>

            {/* 5. Teklif Kârlılığı - PARA BİRİMİ */}
            {offer.profitability && (
              <div className="flex items-start space-x-3">
                <FiTrendingUp className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Teklif Kârlılığı
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formatCurrency(offer.profitability, offer.currency || offer.offer_currency)}
                  </p>
                </div>
              </div>
            )}

            {/* 6. Kârlılık Oranı */}
            {(offer.offer_total || offer.offer_amount) && offer.profitability && (
              <div className="flex items-start space-x-3">
                <FiTrendingUp className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Kârlılık Oranı
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {((Number(offer.profitability) / Number(offer.offer_total || offer.offer_amount)) * 100).toFixed(2)}%
                  </p>
                </div>
              </div>
            )}

            {/* 7. Oluşturulma Tarihi ve Saati */}
            <div className="flex items-start space-x-3">
              <FiCalendar className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Oluşturulma Tarihi ve Saati
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date(offer.created_at).toLocaleString('tr-TR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>

            {/* 8. Oluşturan */}
            {offer.created_by_name && (
              <div className="flex items-start space-x-3">
                <FiUser className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Oluşturan
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {offer.created_by_name}
                  </p>
                </div>
              </div>
            )}

            {/* 9. Dosya Bilgisi */}
            <div className="flex items-start space-x-3">
              <FiDownload className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Ekli Dosya
                </p>
                {(!offer.offer_file_path || offer.offer_file_path.trim() === '') ? (
                  <span className="inline-flex items-center rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-2 py-0.5 text-amber-600 dark:text-amber-400">
                    <FiFileText className="mr-1 h-3 w-3" />
                    <span className="opacity-70">Dosya eklenmemiştir</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-2 py-0.5 text-green-600 dark:text-green-400">
                    <button
                      onClick={handleDownloadFile}
                      className="flex items-center hover:bg-green-200 dark:hover:bg-green-800 rounded px-1 py-0.5 mr-1"
                      title="Dosyayı indir"
                    >
                      <FiFileText className="mr-1 h-3 w-3" />
                      <span className="opacity-70">{offer.offer_file_name || 'Dosya'}</span>
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm('Dosyayı silmek istediğinizden emin misiniz?')) {
                          try {
                            await offerService.deleteOfferFile(offer.id);
                            toast.success('Dosya silindi');
                            queryClient.invalidateQueries(['offer', id]);
                          } catch (error) {
                            toast.error('Dosya silinemedi');
                          }
                        }
                      }}
                      className="hover:bg-green-200 dark:hover:bg-green-800 rounded-full p-0.5"
                      title="Dosyayı sil"
                    >
                      <FiX className="h-3 w-3" />
                    </button>
                  </span>
                )}
              </div>
            </div>

            {/* Ayırıcı çizgi */}
            <div className="border-t border-gray-200 dark:border-gray-600 my-4"></div>

            {/* 10. Müşteri */}
            <div className="flex items-start space-x-3">
              <FiUser className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Müşteri
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {offer.customer_name || 'Müşteri bilgisi yok'}
                </p>
              </div>
            </div>

            {/* 11. Proje */}
            {offer.project_name && (
              <div className="flex items-start space-x-3">
                <FiTag className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Proje
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {offer.project_name}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Durum Güncelleme Modal'ı */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Durum Güncelle
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Teklif durumunu "{getStatusText(newStatus)}" olarak güncellemek istediğinizden emin misiniz?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="btn-secondary"
                disabled={updateStatusMutation.isLoading}
              >
                İptal
              </button>
              <button
                onClick={confirmStatusUpdate}
                className="btn-primary"
                disabled={updateStatusMutation.isLoading}
              >
                {updateStatusMutation.isLoading ? 'Güncelleniyor...' : 'Güncelle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Silme Onay Modal'ı */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Teklifi Sil"
        message={`#${offer.id} numaralı teklifi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        isLoading={deleteOfferMutation.isLoading}
      />

      {/* Teklif Düzenleme Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-lg">
            <div className="modal-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Teklifi Düzenle</h3>
                <button onClick={() => setShowEditModal(false)} className="text-white hover:text-gray-300">
                  <FiX size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Durum</label>
                  <select
                    className="input-field w-full"
                    value={offerStatus}
                    onChange={(e) => setOfferStatus(e.target.value)}
                  >
                    <option>Hazırlandı</option>
                    <option>Gönderildi</option>
                    <option>Müşteri Onay Sürecinde</option>
                    <option>Müşteri Revize İstedi</option>
                    <option>Teklif Kabul Edilmedi</option>
                    <option>Teklif Onaylandı</option>
                    <option>Revize Gönderilecek</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Para Birimi</label>
                  <select
                    className="input-field w-full"
                    value={offerCurrency}
                    onChange={(e) => setOfferCurrency(e.target.value)}
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teklif Tutarı</label>
                  <CurrencyInput
                    id="offerTotal"
                    name="offerTotal"
                    placeholder="0.00"
                    value={offerTotal}
                    onValueChange={(value, name) => {
                      setOfferTotal(value);
                    }}
                    prefix={offerCurrency === 'USD' ? '$' : offerCurrency === 'EUR' ? '€' : '₺'}
                    decimalsLimit={2}
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kârlılık</label>
                  <CurrencyInput
                    id="profitability"
                    name="profitability"
                    placeholder="0.00"
                    value={offerProfitability}
                    onValueChange={(value, name) => {
                      setOfferProfitability(value);
                    }}
                    prefix={offerCurrency === 'USD' ? '$' : offerCurrency === 'EUR' ? '€' : '₺'}
                    decimalsLimit={2}
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gönderilme Tarihi</label>
                  <input
                    type="date"
                    value={offerDate}
                    onChange={(e) => setOfferDate(e.target.value)}
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gönderilme Saati</label>
                  <input
                    type="time"
                    value={offerTime}
                    onChange={(e) => setOfferTime(e.target.value)}
                    className="input-field w-full"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teklif Dosyası (Opsiyonel)</label>
                  <input
                    type="file"
                    onChange={(e) => setOfferFile(e.target.files[0])}
                    className="input-field w-full"
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teklif Açıklaması</label>
                  <textarea
                    value={offerDescription}
                    onChange={(e) => setOfferDescription(e.target.value)}
                    className="input-field w-full"
                    rows={3}
                    placeholder="Teklif açıklaması..."
                  />
                </div>
              </div>

              {uploadMessage && (
                <div className={`mt-4 p-3 rounded-md ${
                  uploadMessageType === 'error' 
                    ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300' 
                    : 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                }`}>
                  {uploadMessage}
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary"
                  disabled={isUploading}
                >
                  İptal
                </button>
                <button
                  onClick={handleUpdateOffer}
                  className="btn-primary"
                  disabled={isUploading}
                >
                  {isUploading ? 'Güncelleniyor...' : 'Güncelle'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferDetail;
